import * as pc from 'playcanvas';

/**
 * Loads photoscanned glTF props (Poly Haven, CC0 — public/props/LICENSE.txt)
 * and instances them at a real-world height. Props are ambience, not data:
 * they stream in after boot and the world never waits on them.
 */

const containerCache = new Map<string, Promise<pc.Asset | null>>();

function loadContainer(app: pc.Application, url: string): Promise<pc.Asset | null> {
  let pending = containerCache.get(url);
  if (pending) return pending;
  pending = new Promise((resolve) => {
    const asset = new pc.Asset(url, 'container', { url });
    asset.once('load', () => resolve(asset));
    asset.once('error', (err: unknown) => {
      console.warn(`[GOC props] failed to load ${url}:`, err);
      resolve(null);
    });
    app.assets.add(asset);
    app.assets.load(asset);
  });
  containerCache.set(url, pending);
  return pending;
}

/**
 * Returns a fresh instance of a scanned prop (cached per url), uniformly scaled
 * to `height` meters with its base at local y = 0. Null if loading failed.
 * `nodeName` picks a single named piece out of a multi-piece kit scan (e.g. the
 * assembled `seat_bench` out of the modular street-seating parts spread).
 */
export async function spawnProp(
  app: pc.Application,
  url: string,
  height: number,
  nodeName?: string,
): Promise<pc.Entity | null> {
  const asset = await loadContainer(app, url);
  if (!asset?.resource) return null;

  const resource = asset.resource as pc.ContainerResource;
  let model = resource.instantiateRenderEntity();
  if (nodeName) {
    const piece = model.findByName(nodeName) as pc.Entity | null;
    if (piece) {
      piece.setLocalPosition(0, 0, 0);
      model = piece;
    }
  }

  const aabb = new pc.BoundingBox();
  let has = false;
  for (const render of model.findComponents('render') as pc.RenderComponent[]) {
    for (const mi of render.meshInstances) {
      if (!has) {
        aabb.copy(mi.aabb);
        has = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  if (!has) return null;

  const min = aabb.getMin();
  const naturalHeight = Math.max(aabb.halfExtents.y * 2, 0.0001);
  model.setLocalPosition(-aabb.center.x, -min.y, -aabb.center.z);

  const wrapper = new pc.Entity('prop');
  wrapper.addChild(model);
  const s = height / naturalHeight;
  wrapper.setLocalScale(s, s, s);
  return wrapper;
}
